import { EmployerBankSettingsRequest, EmployerBankSettingsResponse, EmployerBankSettingsState, FetchState, MassTransitManualClaimStatus } from "@/shared/types";
import * as BankService from "@/shared/api/bankService";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";

export const initialState: EmployerBankSettingsState = {
    fetchState: FetchState.Uninitialized,
    employerId: null,
    bankSettings: null
};
export const useEmployerBankSettingsStore = defineStore("employerBankSettings", {
    state: (): EmployerBankSettingsState => (_.cloneDeep(initialState)),
    getters: {
        isLoaded: state => state.fetchState === FetchState.Loaded,
        settings: state => state.bankSettings,
        employerHasMassTransitPlan: state => state.bankSettings
            && state.bankSettings.hasMassTransitPlan,
        employerMassTransitPlanStatus: state => state.bankSettings
            ? state.bankSettings.status
            : MassTransitManualClaimStatus.Inactive,
        isEmployerMassTransitPlanStatusActive: state => state.bankSettings
            ? state.bankSettings.status === MassTransitManualClaimStatus.Active
            : false,
        currentEmployerId: state => state.employerId,
        displayRetailContributionDisclaimer: state => state.bankSettings.enableRetailContributionWarning
    },
    actions: {
        resetState () {
            this.employerId = null;
            this.fetchState = FetchState.Uninitialized;
        },
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setSettings (data: EmployerBankSettingsResponse) {
            this.bankSettings = data;
            this.employerId = data.employerId;
        },
        async getEmployerBankSettings (options: {employerId: number, force?: boolean}) {
            if (!options.force && this.fetchState !== FetchState.Uninitialized
                && this.employerId === options.employerId) {
                return Promise.resolve();
            }
            this.setFetchState(FetchState.Loading);
            return BankService.getEmployerBankSettings(options.employerId)
                .then(async response => {
                    this.setSettings(response.data);
                    this.setFetchState(FetchState.Loaded);
                }).catch(err => {
                    this.resetState();
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        async getEmployerBankSettingsForCurrentUser (force?: boolean) {
            if (!force && this.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            this.setFetchState(FetchState.Loading);
            return BankService.getEmployerBankSettingsForCurrentUser()
                .then(async response => {
                    this.setSettings(response.data);
                    this.setFetchState(FetchState.Loaded);
                }).catch(err => {
                    this.resetState();
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        async putEmployerBankSettings (request: EmployerBankSettingsRequest) {
            this.setFetchState(FetchState.Loading);
            return BankService.putEmployerBankSettings(request)
            .then(async response => {
                this.setSettings(response.data);
                this.setFetchState(FetchState.Loaded);
            }).catch(err => {
                this.resetState();
                return storeHelper.handleError({ error: err, rethrow: true });
            });
        }
    }
});